import type { ThreadEvent } from "@/schemas/threadEvent.schema"
import type { WaitingConversation } from "@/stores/waitingConversationStore"
import {
    isMessageReceivedEvent,
    isMessageSentEvent,
    isWilsonAuthoredThreadEvent,
} from "@/utils/threadEvent.utils"
import type { ConversationsSnapshot } from "../types/chatInterface.types"
import { withoutMessageStamp } from "./answerText.utils"

type WaitingConversationStatus = "working" | "replied" | "idle"

const PREVIEW_MAX_CHARS = 280

export function waitingConversationFromSnapshot({
    snapshot,
    closedAt,
}: {
    snapshot: ConversationsSnapshot | null
    closedAt: number
}): WaitingConversation | null {
    if (!snapshot || snapshot.isDraft || !snapshot.activeId) return null
    const active = snapshot.conversations.find(
        conversation => conversation.id === snapshot.activeId
    )
    if (!active) return null
    return {
        threadId: active.id,
        title: active.title,
        question: snapshot.question ?? null,
        isWorkingAtClose: active.isWorking,
        closedAt,
    }
}

type LastQuestionState = "answered" | "unanswered" | "pending"

const newestFirst = (events: readonly ThreadEvent[]): ThreadEvent[] =>
    [...events].sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

const isWilsonAnswer = (event: ThreadEvent): boolean =>
    isMessageSentEvent(event) &&
    isWilsonAuthoredThreadEvent(event) &&
    !!event.markdownContent?.trim()

// Browser and server clocks drift a little; an answer this close to the close
// still counts as the one the run was working on.
const CLOCK_SKEW_MS = 5_000

const answeredBefore = (
    answer: ThreadEvent,
    closedAt: number | undefined
): boolean =>
    closedAt !== undefined &&
    new Date(answer.createdAt).getTime() < closedAt - CLOCK_SKEW_MS

const isQuestionAtClose = (
    event: ThreadEvent | undefined,
    questionAtClose: string
): boolean =>
    !!event?.markdownContent &&
    plainTextPreview(event.markdownContent) === questionAtClose

// A run that was working at close cannot have answered before it, so an older
// answer belongs to an earlier turn whose question may not be stored yet. The
// question on screen at close names the turn: until the thread stores it, any
// answer is the previous turn's.
function lastQuestionState(
    events: readonly ThreadEvent[],
    closedAt?: number,
    questionAtClose?: string | null
): LastQuestionState {
    const sorted = newestFirst(events)
    const question = sorted.find(isMessageReceivedEvent)
    if (questionAtClose && !isQuestionAtClose(question, questionAtClose))
        return "pending"
    const newest = sorted.find(isWilsonAnswer)
    const answer =
        newest && !answeredBefore(newest, closedAt) ? newest : undefined
    if (!question)
        return answer || closedAt === undefined ? "answered" : "pending"
    if (question.isUnanswered) return "unanswered"
    if (!answer) return "pending"
    return new Date(answer.createdAt).getTime() >=
        new Date(question.createdAt).getTime()
        ? "answered"
        : "pending"
}

export const RUN_REGISTER_GRACE_MS = 20_000

export function waitingConversationStatus({
    isWorkingAtClose,
    activeRun,
    hasRunCheckFailed,
    runCheckedMsAfterClose,
    hasEventsSinceRunGone,
    events,
    closedAt,
    questionAtClose,
}: {
    isWorkingAtClose: boolean
    activeRun: unknown
    hasRunCheckFailed: boolean
    runCheckedMsAfterClose: number
    hasEventsSinceRunGone: boolean
    events: readonly ThreadEvent[] | undefined
    closedAt?: number
    questionAtClose?: string | null
}): WaitingConversationStatus {
    if (!isWorkingAtClose) return "idle"
    if (hasRunCheckFailed) {
        return events &&
            lastQuestionState(events, closedAt, questionAtClose) === "answered"
            ? "replied"
            : "working"
    }
    if (activeRun !== null || !events) return "working"
    const state = lastQuestionState(events, closedAt, questionAtClose)
    if (state === "answered") return "replied"
    if (state === "unanswered") return "idle"
    return hasEventsSinceRunGone &&
        runCheckedMsAfterClose >= RUN_REGISTER_GRACE_MS
        ? "idle"
        : "working"
}

export function plainTextPreview(markdown: string): string {
    const text = withoutMessageStamp(markdown)
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
        .replace(/^\s*\|?[\s:-]+\|[\s|:-]*$/gm, " ")
        .replace(/[|*_`~]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    return text.length > PREVIEW_MAX_CHARS
        ? `${text.slice(0, PREVIEW_MAX_CHARS - 1).trimEnd()}…`
        : text
}

export function latestWilsonAnswer(
    events: readonly ThreadEvent[]
): { text: string; createdAt: string } | null {
    const answer = newestFirst(events).find(isWilsonAnswer)
    if (!answer?.markdownContent) return null
    const text = plainTextPreview(answer.markdownContent)
    return text ? { text, createdAt: answer.createdAt } : null
}

export function latestWilsonAnswerMarkdown(
    events: readonly ThreadEvent[]
): string | null {
    const answer = newestFirst(events).find(isWilsonAnswer)
    return answer?.markdownContent?.trim() ? answer.markdownContent : null
}

export function latestQuestionText(
    events: readonly ThreadEvent[]
): string | null {
    const question = newestFirst(events).find(
        event =>
            isMessageReceivedEvent(event) && !!event.markdownContent?.trim()
    )
    return question?.markdownContent
        ? plainTextPreview(question.markdownContent) || null
        : null
}

interface ShownMessage {
    type: string
    createdAt: string
    markdownContent?: string | null
}

export function shownQuestionText(
    messages: readonly ShownMessage[]
): string | null {
    let newest: ShownMessage | null = null
    for (const message of messages) {
        if (message.type !== "messageReceived") continue
        if (!message.markdownContent?.trim()) continue
        if (!newest || message.createdAt >= newest.createdAt) newest = message
    }
    return newest?.markdownContent
        ? plainTextPreview(newest.markdownContent) || null
        : null
}

export function waitingQuestionText(
    events: readonly ThreadEvent[],
    closedAt: number
): string | null {
    const sorted = newestFirst(events)
    const question = sorted.find(
        event =>
            isMessageReceivedEvent(event) && !!event.markdownContent?.trim()
    )
    if (!question) return null
    const answer = sorted.find(isWilsonAnswer)
    const isStale =
        !!answer &&
        new Date(answer.createdAt).getTime() >=
            new Date(question.createdAt).getTime() &&
        answeredBefore(answer, closedAt)
    return isStale ? null : latestQuestionText([question])
}

export function waitingConversationHeadline({
    status,
    events,
}: {
    status: WaitingConversationStatus
    events: readonly ThreadEvent[]
}): string | null {
    if (status === "working") return latestQuestionText(events)
    if (status === "replied") return latestWilsonAnswer(events)?.text ?? null
    const newest = newestFirst(events).find(
        event =>
            (isMessageReceivedEvent(event) &&
                !!event.markdownContent?.trim()) ||
            isWilsonAnswer(event)
    )
    return newest?.markdownContent
        ? plainTextPreview(newest.markdownContent) || null
        : null
}

const RELATIVE_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> =
    [
        { unit: "day", ms: 86_400_000 },
        { unit: "hour", ms: 3_600_000 },
        { unit: "minute", ms: 60_000 },
    ]

export function relativeTimeLabel({
    at,
    now,
    locale,
}: {
    at: Date
    now: Date
    locale: string
}): string {
    const elapsed = Math.max(0, now.getTime() - at.getTime())
    const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
    const match = RELATIVE_UNITS.find(({ ms }) => elapsed >= ms)
    if (!match) return format.format(0, "second")
    return format.format(-Math.floor(elapsed / match.ms), match.unit)
}
